/* Words, not codes. Hospital staff read this app; a code the server sends is
 * shown through one of these maps, and an unknown code falls back to a tidied
 * version of itself rather than disappearing. */

import { ApiError } from './api';

function tidy(code: string): string {
  const s = code.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function lookup(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '—';
  return map[code] ?? tidy(code);
}

const SERVICES: Record<string, string> = {
  NURSE: 'Nurse at home',
  HOME_ICU: 'ICU at home',
  PHYSIO: 'Physiotherapy',
  AMBULANCE: 'Ambulance',
  DOCTOR: 'Doctor consultation',
  EQUIPMENT: 'Medical equipment',
};
export const serviceName = (c: string | null | undefined) => lookup(SERVICES, c);
/** The job's own word: an attendant is a NURSE item on the ATTENDANT tier
 *  (000032), and an agency must not read it as a nursing job. */
export const jobName = (c: string | null | undefined, variant?: string | null) =>
  c === 'NURSE' && variant === 'ATTENDANT' ? 'Attendant at home' : serviceName(c);

const OFFER_STATUS: Record<string, string> = {
  OFFERED: 'Waiting for you',
  QUOTED: 'Quote sent',
  ACCEPTED: 'Yours',
  DECLINED: 'Declined',
  EXPIRED: 'Time ran out',
  WITHDRAWN: 'Withdrawn',
};
export const offerStatus = (c: string | null | undefined) => lookup(OFFER_STATUS, c);

const ITEM_STATUS: Record<string, string> = {
  AGENCY_ACCEPTED: 'Needs staff',
  ASSIGNED: 'Staff assigned',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};
export const itemStatus = (c: string | null | undefined) => lookup(ITEM_STATUS, c);

const ASSIGNMENT_STATUS: Record<string, string> = {
  PROPOSED: 'Waiting for them to accept',
  ACCEPTED: 'Accepted',
  ACTIVE: 'On the job',
  COMPLETED: 'Done',
  REJECTED: 'Turned it down',
  REPLACED: 'Replaced',
  NO_SHOW: 'Did not arrive',
  CANCELLED: 'Cancelled',
};
export const assignmentStatus = (c: string | null | undefined) => lookup(ASSIGNMENT_STATUS, c);

const STAFF_STATUS: Record<string, string> = {
  PENDING_VERIFICATION: 'Papers being checked',
  ACTIVE: 'Working',
  SUSPENDED: 'Suspended',
  REJECTED: 'Not accepted',
  LEFT: 'Left',
};
export const staffStatus = (c: string | null | undefined) => lookup(STAFF_STATUS, c);

const STAFF_TYPE: Record<string, string> = {
  // CLINICAL is a nurse OR a physiotherapist (000033: the profession is on the
  // qualification, which the team list does not carry).
  CLINICAL: 'Nurse or physiotherapist',
  CARE: 'Attendant',
  LOGISTICS: 'Driver',
  DOCTOR: 'Doctor',
};
export const staffType = (c: string | null | undefined) => lookup(STAFF_TYPE, c);

const ROLES: Record<string, string> = {
  VEHICLE: 'Ambulance',
  DRIVER: 'Driver',
  PARAMEDIC: 'Paramedic',
  ATTENDANT: 'Attendant',
  EQUIPMENT_UNIT: 'Equipment unit',
  OWNER: 'Owner',
  ADMIN: 'Admin',
  DISPATCHER: 'Dispatcher',
  ACCOUNTANT: 'Accountant',
};
/* The server names every person-on-a-shift role NURSE_n, whatever the service:
 * a doctor's consultation and a physiotherapy visit are bound into NURSE_1 too.
 * So the word comes from the job's service when it is known — a doctor is a
 * Doctor on the Assign screen, not a Carer. */
const PERSON_ROLE: Record<string, string> = { DOCTOR: 'Doctor', PHYSIO: 'Physiotherapist' };
export function roleName(c: string | null | undefined, serviceCode?: string | null): string {
  if (c && /^NURSE_\d+$/.test(c)) {
    const n = c.slice(6);
    const word = (serviceCode && PERSON_ROLE[serviceCode]) || 'Carer';
    return n === '1' ? word : `${word} ${n}`;
  }
  return lookup(ROLES, c);
}

const SHIFT_PATTERN: Record<string, string> = {
  DAY_12: '12-hour day',
  NIGHT_12: '12-hour night',
  FULL_24: '24 hours',
  CUSTOM: 'Custom hours',
};
export const shiftPattern = (c: string | null | undefined) => lookup(SHIFT_PATTERN, c);

const MONEY_STATUS: Record<string, string> = { ACCRUED: 'Owed to you', PAID: 'Paid', VOID: 'Cancelled', SCHEDULED: 'Scheduled' };
export const moneyStatus = (c: string | null | undefined) => lookup(MONEY_STATUS, c);

/* The bind refusals (binding/errors.go and the §3 gate). The server's own
 * message is kept beside the word: it often names the missing document. */
const REFUSALS: Record<string, string> = {
  STAFF_NOT_ACTIVE: 'This person is not cleared to work yet.',
  STAFF_NOT_AT_AGENCY: 'This person does not work for your agency.',
  STAFF_AGENCY_NOT_CONFIRMED: 'You have not confirmed this person works for you yet (see Team).',
  STAFF_DOCUMENT_BLOCKING: 'A required document is missing or expired.',
  STAFF_QUALIFICATION_INSUFFICIENT: 'Their qualification is not enough for this job.',
  STAFF_WRONG_ROLE: 'This person cannot do this role: a driver drives, a nurse nurses, a doctor sees the patient.',
  STAFF_ON_LEAVE: 'They are on approved leave on that day.',
  RESOURCE_OVERLAP: 'Already busy at that time.',
  VEHICLE_NOT_ELIGIBLE: 'This ambulance is not cleared to run (papers or status).',
  VARIANT_MISMATCH: 'This ambulance is not the type the family asked for.',
  OXYGEN_REQUIRED: 'The family needs oxygen on board.',
  VENTILATOR_REQUIRED: 'The family needs a ventilator on board.',
  EQUIPMENT_NOT_ELIGIBLE: 'This equipment unit is not available.',
  NOT_AT_AGENCY: 'This does not belong to your agency.',
  SHIFT_ALREADY_FILLED: 'Someone is already on this shift.',
  INVALID_ROLE_SET: 'That combination of people is not allowed for this job.',
  ITEM_NOT_BINDABLE: 'This job can no longer be staffed.',
  SOFT_CRITERIA_UNMET: 'The family asked for something this person does not match. Tick the box to send them anyway.',
  REPLACE_NOT_LIVE: 'That assignment can no longer be replaced.',
  REPLACE_SAME_RESOURCE: 'That is the same person already assigned.',
  ASSIGNMENT_STARTED: 'This visit has already started and cannot be withdrawn.',
  TRY_AGAIN: 'Someone else changed this job at the same moment. Try again.',
  ITEM_ALREADY_TAKEN: 'Another provider took this request first.',
  OFFER_EXPIRED: 'The time to answer has run out.',
  OFFER_NOT_OPEN: 'This request is no longer open.',
  ITEM_NOT_BROADCASTING: 'This request is no longer open.',
  PLATFORM_PRICE_MISSING: 'The platform has not set a price for this service in this city yet, so it cannot be accepted. Decline it or let it pass.',
  PLATFORM_PRICED: 'This request has a fixed platform price. Accept it instead.',
  TOO_CLOSE_TO_START: 'The appointment is too close now to be accepted.',
};
/* The papers a refusal names (document_types.code), in words. */
const DOCUMENTS: Record<string, string> = {
  STAFF_IDENTITY_PROOF: 'ID proof',
  STAFF_POLICE_VERIF: 'Police verification',
  STAFF_NURSING_COUNCIL: 'Nursing council registration',
  STAFF_MEDICAL_COUNCIL: 'Medical council registration',
  STAFF_QUALIFICATION: 'Qualification certificate',
  VEHICLE_RC: 'Registration certificate (RC)',
  VEHICLE_FITNESS: 'Fitness certificate',
  VEHICLE_INSURANCE: 'Insurance',
  VEHICLE_PERMIT: 'Permit',
  VEHICLE_PUC: 'Pollution certificate (PUC)',
};
export const documentName = (c: string | null | undefined) => lookup(DOCUMENTS, c);

/* A vehicle refusal's reason (binding/eligible.go eligibleVehicle). */
const VEHICLE_REASONS: Record<string, string> = {
  NOT_ACTIVE: 'This ambulance is not active.',
  NOT_AT_AGENCY: 'This ambulance does not belong to your agency.',
  VARIANT_MISMATCH: 'Not the type of ambulance the family asked for.',
  OXYGEN_REQUIRED: 'The family needs oxygen on board.',
  VENTILATOR_REQUIRED: 'The family needs a ventilator on board.',
};

/** One refusal as a sentence: a missing paper is named in words, never as
 *  its code, and the server's message is shown only where it adds a fact. */
export function refusalText(code: string, reason?: string): string {
  if (code === 'STAFF_DOCUMENT_BLOCKING' && reason && DOCUMENTS[reason]) return `${DOCUMENTS[reason]} is missing or expired.`;
  if (code === 'VEHICLE_NOT_ELIGIBLE' && reason) {
    if (VEHICLE_REASONS[reason]) return VEHICLE_REASONS[reason];
    if (reason.startsWith('DOCUMENT_')) return `${documentName(reason.slice('DOCUMENT_'.length))} is missing or expired.`;
  }
  if (code === 'STAFF_WRONG_ROLE') return REFUSALS[code]!;
  const w = REFUSALS[code];
  if (w && reason && reason !== w && !/^Resource \S+ is not eligible/.test(reason)) return `${w} (${reason})`;
  return w ?? reason ?? tidy(code);
}

/** The message to show for any failed call. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (REFUSALS[e.code]) {
      // A bind refusal names the paper in its body (binding.go refusal).
      const doc = typeof e.body.document_code === 'string' ? e.body.document_code : undefined;
      return refusalText(e.code, doc ?? e.message);
    }
    return e.message || tidy(e.code);
  }
  return 'Something went wrong. Try again.';
}

/** Soft criteria a candidate does not meet (binding/eligible.go softMatches). */
export function unmetText(c: string): string {
  if (c === 'GENDER_PREFERENCE_UNMET') return 'Not the gender the family asked for';
  if (c === 'LANGUAGE_PREFERENCE_UNMET') return 'Does not speak the language the family asked for';
  if (c.startsWith('CARE_TASK_SKILL_UNMET:')) return `Not trained for: ${tidy(c.slice('CARE_TASK_SKILL_UNMET:'.length))}`;
  return tidy(c);
}

/** Ambulance variants (service_variants under AMBULANCE). */
const AMBULANCE_TYPE: Record<string, string> = {
  BLS: 'Basic life support',
  ALS: 'Advanced life support',
  ICU: 'ICU ambulance',
  PATIENT_TRANSPORT: 'Patient transport',
  MORTUARY: 'Mortuary transport',
};
export const ambulanceType = (c: string | null | undefined) => lookup(AMBULANCE_TYPE, c);
